import React from 'react';
import { clsx } from 'clsx';
import {
  PlusIcon,
  PencilIcon,
  TrashIcon,
  UserGroupIcon,
  ClipboardDocumentListIcon,
  ChevronDownIcon,
  ChevronRightIcon,
} from '@heroicons/react/24/outline';
import { PlanRectangle } from '../../services/planService';

interface RectangleNodeProps {
  rectangle: PlanRectangle;
  children: PlanRectangle[];
  allRectangles: PlanRectangle[];
  selectedId: number | null;
  expandedIds: Set<number>;
  onSelect: (rectangle: PlanRectangle) => void;
  onToggleExpand: (id: number) => void;
  onAddChild: (parent: PlanRectangle) => void;
  onEdit: (rectangle: PlanRectangle) => void;
  onDelete: (rectangle: PlanRectangle) => void;
  level?: number;
}

const RectangleNode: React.FC<RectangleNodeProps> = ({
  rectangle,
  children,
  allRectangles,
  selectedId,
  expandedIds,
  onSelect,
  onToggleExpand,
  onAddChild,
  onEdit,
  onDelete,
  level = 0,
}) => {
  const hasChildren = children.length > 0;
  const isExpanded = expandedIds.has(rectangle.id);
  const isSelected = selectedId === rectangle.id;
  const hasLinkedUsers = rectangle.linkedUserIds && rectangle.linkedUserIds.length > 0;
  const hasLinkedTasks = rectangle.linkedTaskIds && rectangle.linkedTaskIds.length > 0;

  // Get contrasting text color based on background
  const getContrastColor = (hexColor: string) => {
    const hex = hexColor.replace('#', '');
    const r = parseInt(hex.substr(0, 2), 16);
    const g = parseInt(hex.substr(2, 2), 16);
    const b = parseInt(hex.substr(4, 2), 16);
    const luminance = (0.299 * r + 0.587 * g + 0.114 * b) / 255;
    return luminance > 0.5 ? '#1f2937' : '#ffffff';
  };

  const textColor = getContrastColor(rectangle.color);

  return (
    <div className={clsx('select-none', level > 0 && 'ml-6 border-l-2 border-gray-200 pl-4')}>
      {/* Node */}
      <div
        onClick={() => onSelect(rectangle)}
        className={clsx(
          'group relative flex items-center gap-2 p-3 rounded-xl cursor-pointer transition-all duration-200',
          'border-2 shadow-sm hover:shadow-md',
          isSelected
            ? 'ring-2 ring-primary-500 ring-offset-2'
            : 'hover:ring-1 hover:ring-gray-300'
        )}
        style={{
          backgroundColor: rectangle.color,
          borderColor: `${rectangle.color}dd`,
        }}
      >
        {/* Expand/Collapse */}
        {hasChildren && (
          <button
            onClick={(e) => {
              e.stopPropagation();
              onToggleExpand(rectangle.id);
            }}
            className="p-1 rounded-md hover:bg-black/10 transition-colors"
            style={{ color: textColor }}
          >
            {isExpanded ? (
              <ChevronDownIcon className="h-4 w-4" />
            ) : (
              <ChevronRightIcon className="h-4 w-4" />
            )}
          </button>
        )}

        {/* Content */}
        <div className="flex-1 min-w-0">
          <h4 className="font-semibold truncate" style={{ color: textColor }}>
            {rectangle.title}
          </h4>
          {rectangle.content && (
            <p
              className="text-sm mt-0.5 line-clamp-2 opacity-80"
              style={{ color: textColor }}
            >
              {rectangle.content}
            </p>
          )}
          {/* Links indicators */}
          {(hasLinkedUsers || hasLinkedTasks) && (
            <div className="flex items-center gap-2 mt-1.5">
              {hasLinkedUsers && (
                <span
                  className="inline-flex items-center gap-1 text-xs px-1.5 py-0.5 rounded-full bg-white/20"
                  style={{ color: textColor }}
                >
                  <UserGroupIcon className="h-3 w-3" />
                  {rectangle.linkedUserIds.length}
                </span>
              )}
              {hasLinkedTasks && (
                <span
                  className="inline-flex items-center gap-1 text-xs px-1.5 py-0.5 rounded-full bg-white/20"
                  style={{ color: textColor }}
                >
                  <ClipboardDocumentListIcon className="h-3 w-3" />
                  {rectangle.linkedTaskIds.length}
                </span>
              )}
            </div>
          )}
        </div>

        {/* Actions */}
        <div className="flex items-center gap-1 opacity-0 group-hover:opacity-100 transition-opacity">
          <button
            onClick={(e) => { e.stopPropagation(); onAddChild(rectangle); }}
            className="p-1.5 rounded-lg bg-white/20 hover:bg-white/40 transition-colors"
            style={{ color: textColor }}
            title="Add child"
          >
            <PlusIcon className="h-4 w-4" />
          </button>
          <button
            onClick={(e) => { e.stopPropagation(); onEdit(rectangle); }}
            className="p-1.5 rounded-lg bg-white/20 hover:bg-white/40 transition-colors"
            style={{ color: textColor }}
            title="Edit"
          >
            <PencilIcon className="h-4 w-4" />
          </button>
          {children.length === 0 && (
            <button
              onClick={(e) => { e.stopPropagation(); onDelete(rectangle); }}
              className="p-1.5 rounded-lg bg-white/20 hover:bg-red-500/80 hover:text-white transition-colors"
              style={{ color: textColor }}
              title="Delete"
            >
              <TrashIcon className="h-4 w-4" />
            </button>
          )}
        </div>
      </div>

      {/* Children */}
      {hasChildren && isExpanded && (
        <div className="mt-2 space-y-2">
          {children.map((child) => (
            <RectangleNode
              key={child.id}
              rectangle={child}
              children={allRectangles.filter((r) => r.parentId === child.id)}
              allRectangles={allRectangles}
              selectedId={selectedId}
              expandedIds={expandedIds}
              onSelect={onSelect}
              onToggleExpand={onToggleExpand}
              onAddChild={onAddChild}
              onEdit={onEdit}
              onDelete={onDelete}
              level={level + 1}
            />
          ))}
        </div>
      )}
    </div>
  );
};

export default RectangleNode;

